import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, MapPin, Home, Building2, Phone, MessageCircle, Heart, Eye, BedDouble, Bath, Ruler, Compass,
  FileText, Download, User, LogOut, Bell, Menu, X, ChevronRight, Star, ShieldCheck, CheckCircle2, Loader2,
  Landmark, KeyRound, CalendarClock, IndianRupee, BadgeCheck, Sparkles, Sunrise, Sunset, ArrowUp, ArrowDown, Route, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fileUrl } from "../../lib/api";
import ReMember from "./org/ReMember";
import { getCustomerToken, setCustomerSession, clearCustomerSession, getCustomerInfo } from "../../lib/customer";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const RE_CSS = `
.re-root{font-family:var(--re-body)} .re-h{font-family:var(--re-font)}
.re-card{border-radius:var(--re-radius);overflow:hidden;background:#fff;border:1px solid color-mix(in srgb,var(--re-t) 10%,transparent);transition:transform .25s ease,box-shadow .25s ease}
.re-card:hover{transform:translateY(-4px);box-shadow:0 20px 40px -18px color-mix(in srgb,var(--re-t) 35%,transparent)}
.re-btn{background:var(--re-p);color:#fff;border-radius:999px;font-weight:700;transition:filter .2s,transform .15s}
.re-btn:hover{filter:brightness(1.1)} .re-btn:active{transform:scale(.97)}
.re-chip{border:1px solid color-mix(in srgb,var(--re-t) 18%,transparent);border-radius:999px;font-weight:600}
.re-dark{background:var(--re-bg);color:var(--re-t)}
.re-dark .re-card{background:color-mix(in srgb,var(--re-bg) 92%,#fff);border-color:color-mix(in srgb,var(--re-t) 14%,transparent)}
@keyframes re-ken{0%{transform:scale(1)}100%{transform:scale(1.08)}}
.re-kenburns{animation:re-ken 14s ease-in-out infinite alternate}
.re-mitem{display:flex;align-items:center;gap:.75rem;width:100%;padding:.7rem .8rem;border-radius:.75rem;font-weight:600;font-size:15px;color:var(--re-t);text-align:left;transition:background-color .15s ease}
.re-mitem:hover{background:color-mix(in srgb,var(--re-p) 10%,transparent)}
.re-micon{width:2.25rem;height:2.25rem;border-radius:.65rem;display:flex;align-items:center;justify-content:center;flex-shrink:0;background:color-mix(in srgb,var(--re-p) 12%,transparent);color:var(--re-p)}
`;

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const inr2 = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ytEmbed = (u) => {
  const m = (u || "").match(/(?:youtube\.com\/(?:watch\?[^#]*v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};
const igEmbed = (u) => {
  const m = (u || "").match(/instagram\.com\/(?:reels?|p)\/([\w-]{5,})/);
  return m ? `https://www.instagram.com/${u.includes("reel") ? "reel" : "p"}/${m[1]}/embed` : null;
};

const VARIANT = {
  premium: { hero: "full", dark: false },
  modern: { hero: "split", dark: false },
  luxury: { hero: "editorial", dark: true },
  smart: { hero: "split", dark: false },
  marketplace: { hero: "full", dark: false },
};

const CAT_ICON = { "Sites / Plots": MapPin, "Individual Houses": Home, Flats: Building2, Apartments: Building2,
  Villas: Landmark, "Commercial Properties": IndianRupee, "Open Plots": MapPin, "Agricultural Land": Sparkles, Other: KeyRound };

function useReData(slug, draft = "") {
  const [data, setData] = useState(null);
  useEffect(() => { if (slug === "demo") return; api.get(`/re/public/${slug}/home${draft ? `?draft=${encodeURIComponent(draft)}` : ""}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, [slug, draft]);
  return data;
}

const DEMO_PROPS = [
  { id: "d1", title: "200 Sq Yards Plot", category: "Sites / Plots", status: "For Sale", price: 1800000, price_per_sqyd: 9000, facing: "East", views: 120, images: ["https://images.unsplash.com/photo-1774697443203-0f54409d1613?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"], location: { area: "Nellimarla", district: "Vizianagaram", state: "Andhra Pradesh" }, areas: { total_sqyds: 200 }, bhk: 0, bathrooms: 0 },
  { id: "d2", title: "3 BHK Individual House", category: "Individual Houses", status: "For Sale", price: 8500000, facing: "West", views: 84, images: ["https://images.unsplash.com/photo-1626249893783-cc4a9f66880a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"], location: { area: "Seethammadhara", district: "Visakhapatnam", state: "Andhra Pradesh" }, areas: { total_sqft: 1800 }, bhk: 3, bathrooms: 3 },
  { id: "d3", title: "2 BHK Apartment", category: "Apartments", status: "For Sale", price: 6500000, facing: "North", views: 96, images: ["https://images.unsplash.com/photo-1515263487990-61b07816b324?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"], location: { area: "Madhurawada", district: "Visakhapatnam", state: "Andhra Pradesh" }, areas: { total_sqft: 1200 }, bhk: 2, bathrooms: 2 },
  { id: "d4", title: "300 Sq Yards Plot", category: "Sites / Plots", status: "For Sale", price: 2500000, price_per_sqyd: 8333, facing: "South", views: 61, images: ["https://images.unsplash.com/photo-1773215023063-e662ea91a69c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"], location: { area: "Bheemili", district: "Visakhapatnam", state: "Andhra Pradesh" }, areas: { total_sqyds: 300 }, bhk: 0, bathrooms: 0 },
  { id: "d5", title: "4 BHK Luxury Villa", category: "Villas", status: "For Sale", price: 16000000, facing: "East", views: 142, featured: true, images: ["https://images.unsplash.com/photo-1715168437311-18b9ec0830c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"], location: { area: "Rushikonda", district: "Visakhapatnam", state: "Andhra Pradesh" }, areas: { total_sqft: 3200 }, bhk: 4, bathrooms: 4 },
  { id: "d6", title: "Commercial Space — Main Road", category: "Commercial Properties", status: "For Sale", price: 12000000, facing: "North-East", views: 55, images: ["https://images.unsplash.com/photo-1624204386084-dd8c05e32226?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"], location: { area: "Dwaraka Nagar", district: "Visakhapatnam", state: "Andhra Pradesh" }, areas: { total_sqft: 2400 }, bhk: 0, bathrooms: 0 },
];

function buildDemoData(theme) {
  return {
    website: { id: "demo", name: "MDR Real Estates", slug: "demo" },
    settings: { phone: "+91 98765 43210", email: "hello@mdrrealty.in", address: "Seethammadhara, Visakhapatnam, AP", whatsapp_number: "919876543210" },
    branding: {}, theme,
    featured: DEMO_PROPS.filter((p) => p.featured), latest: DEMO_PROPS,
    categories: [{ name: "Sites / Plots", count: 12 }, { name: "Individual Houses", count: 8 }, { name: "Apartments", count: 15 }, { name: "Villas", count: 5 }, { name: "Commercial Properties", count: 6 }],
    districts: ["Visakhapatnam", "Vizianagaram"],
    facings: ["East", "West", "North", "South"],
    all_categories: ["Sites / Plots", "Individual Houses", "Flats", "Apartments", "Villas", "Commercial Properties", "Open Plots", "Agricultural Land", "Other"],
    facing_options: ["East", "West", "North", "South", "North-East", "North-West", "South-East", "South-West"],
    statuses: ["For Sale", "For Rent", "Sold", "Available"],
  };
}

export default function RESite({ slugOverride, demoTheme, baseOv }) {
  const params = useParams();
  const isDemo = !!demoTheme;
  const slug = isDemo ? "demo" : (slugOverride || params.slug);
  const location = useLocation();
  const navigate = useNavigate();
  const draft = useMemo(() => new URLSearchParams(location.search).get("draft") || "", [location.search]);
  const fetched = useReData(isDemo ? "demo" : slug, draft);
  const data = isDemo ? buildDemoData(demoTheme) : fetched;
  const base = isDemo ? "/demo" : (baseOv !== undefined ? baseOv : (slugOverride ? "/re" : `/site/${slug}/re`));
  const sub = isDemo ? "/" : (location.pathname.startsWith(base) ? (location.pathname.slice(base.length) || "/") : "/");
  const go = isDemo
    ? () => toast.info("Live preview — full navigation works on your published website")
    : (p) => navigate(`${base}${p}`);

  const theme = data?.theme || {};
  const content = data?.content || {};
  const tc = content.theme || {};
  const colors = { ...(theme.colors || {}), ...Object.fromEntries(Object.entries(tc).filter(([k, v]) => v && ["primary", "secondary", "accent", "bg", "text"].includes(k))) };
  const fontH = tc.font || theme.font || "Playfair Display";
  const fontB = tc.body_font || theme.body_font || theme.font || "Manrope";
  const vars = {
    "--re-p": colors.primary || "#1E3A8A", "--re-s": colors.secondary || "#EFF6FF",
    "--re-a": colors.accent || "#D4AF37", "--re-bg": colors.bg || "#FFFFFF", "--re-t": colors.text || "#0F172A",
    "--re-radius": tc.radius || theme.radius || "10px",
    "--re-font": `'${fontH}', serif`,
    "--re-body": `'${fontB}', sans-serif`,
  };
  const vcfg = VARIANT[theme.re_variant] || VARIANT.premium;
  const [cust, setCust] = useState(() => getCustomerInfo(slug));

  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontH)}:wght@500;700;800&family=${encodeURIComponent(fontB)}:wght@400;600;700&display=swap`;
    document.head.appendChild(l);
    return () => document.head.removeChild(l);
  }, [fontH, fontB]);

  useEffect(() => {
    if (!isDemo && content.seo?.title) document.title = content.seo.title;
  }, [content.seo?.title, isDemo]);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  const info = data.settings || {};
  const wa = content.contact?.whatsapp || info.whatsapp_number || info.phone || "";

  const header = (
    <Header data={data} base={base} go={go} sub={sub} cust={cust} vars={vars} dark={vcfg.dark}
      onLogout={() => { clearCustomerSession(slug); localStorage.removeItem(`zylo_remember_${slug}`); localStorage.removeItem(`zylo_remember_${slug}_info`); setCust(null); go("/account"); }} />
  );

  let page;
  if (sub.startsWith("/org")) page = <ReMember slug={slug} data={data} vars={vars} go={go} />;
  else if (sub.startsWith("/property/")) page = <Detail slug={slug} pid={sub.split("/")[2]} data={data} vars={vars} go={go} cust={cust} wa={wa} />;
  else if (sub.startsWith("/properties")) page = <Properties slug={slug} data={data} vars={vars} go={go} cust={cust} />;
  else if (sub.startsWith("/about")) page = <About data={data} vars={vars} go={go} />;
  else if (sub.startsWith("/contact")) page = <Contact slug={slug} data={data} vars={vars} wa={wa} />;
  else if (sub.startsWith("/account")) page = <ReMember slug={slug} data={data} vars={vars} go={go} />;
  else page = <HomePage slug={slug} data={data} vars={vars} go={go} vcfg={vcfg} cust={cust} wa={wa} />;

  return (
    <div className={`re-root min-h-screen ${vcfg.dark ? "re-dark" : ""}`} style={{ ...vars, background: vars["--re-bg"], color: vars["--re-t"] }} data-testid="re-site" data-variant={theme.re_variant}>
      <style>{RE_CSS}</style>
      {header}
      {page}
      <Footer data={data} vars={vars} go={go} wa={wa} />
      {wa && (
        <a href={`https://wa.me/${String(wa).replace(/\D/g, "")}`} target="_blank" rel="noreferrer" data-testid="re-whatsapp-fab"
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
          <MessageCircle className="h-6 w-6" />
        </a>
      )}
    </div>
  );
}

const MENU_ICONS = { Home, Sites: MapPin, "Individual Houses": Landmark, Apartments: Building2, "About Us": User, Contact: Phone };

function Header({ data, base, go, sub, cust, onLogout, dark }) {
  const [open, setOpen] = useState(false);
  const content = data.content || {};
  const name = content.website_name || data.branding?.brand_name || data.website?.name || "Real Estate";
  const links = [
    ["/", "Home"],
    ["/properties?category=Sites%20%2F%20Plots", "Sites"],
    ["/properties?category=Individual%20Houses", "Individual Houses"],
    ["/properties?category=Apartments", "Apartments"],
    ["/about", "About Us"], ["/contact", "Contact"],
  ];
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open]);
  const NavLinks = () => (
    <>
      {links.map(([to, l]) => (
        <button key={l} onClick={() => go(to)} data-testid={`re-nav-${l.toLowerCase().replace(/[\s/]+/g, "-")}`}
          className={`px-3 py-2 rounded-full text-[13px] font-bold transition-colors hover:opacity-100 ${dark ? "text-white/70 hover:bg-white/10" : "hover:bg-black/5 opacity-70 hover:opacity-100"}`}>{l}</button>
      ))}
    </>
  );
  const tint = "color-mix(in srgb, var(--re-t) 10%, transparent)";
  return (
    <>
      <header className={`sticky top-0 z-40 border-b ${dark ? "bg-black/70 border-white/10" : "bg-white/85 border-black/5"} backdrop-blur-xl`} data-testid="re-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2">
          <button className={`lg:hidden ${dark ? "text-white" : ""}`} onClick={() => setOpen(true)} data-testid="re-burger" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
          <button onClick={() => go("/")} className="re-h font-extrabold text-lg tracking-tight shrink-0 flex items-center gap-2" data-testid="re-logo">
            {content.logo_url && <img src={fileUrl(content.logo_url)} alt="" className="h-8 w-8 rounded-lg object-cover" />}{name}</button>
          <nav className="hidden lg:flex items-center gap-0.5 mx-2 overflow-x-auto"><NavLinks /></nav>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {cust ? (
              <>
                <button onClick={() => go("/account")} data-testid="re-nav-account" className="re-btn h-9 px-4 text-xs flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{cust.name?.split(" ")[0]}</button>
                <button onClick={onLogout} data-testid="re-logout" className={`h-9 w-9 rounded-full flex items-center justify-center ${dark ? "bg-white/10 text-white" : "bg-black/5"}`}><LogOut className="h-4 w-4" /></button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="re-nav-orglogin" className={`h-9 px-4 rounded-full text-xs font-bold hidden sm:flex items-center gap-1.5 ${dark ? "border border-white/30 text-white" : "border border-current/30"}`}><Users className="h-3.5 w-3.5" />Org Login</Link>
                <button onClick={() => go("/account")} data-testid="re-nav-login" className={`h-9 px-4 rounded-full text-xs font-bold ${dark ? "border border-white/30 text-white" : "border border-current/30"}`}>Login</button>
              </>
            )}
            <button onClick={() => go("/contact")} data-testid="re-nav-enquire" className="re-btn h-9 px-5 text-xs hidden sm:block">Enquire Now</button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[100] lg:hidden" data-testid="re-mobile-menu">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="re-mobile-menu-overlay" />
            <motion.aside initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween", duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
              className="absolute left-0 inset-y-0 w-[300px] max-w-[85vw] flex flex-col shadow-2xl border-r"
              style={{ background: "var(--re-bg)", color: "var(--re-t)", borderColor: tint }} data-testid="re-mobile-menu-drawer">
              <div className="flex items-center justify-between gap-3 px-5 h-16 shrink-0 border-b" style={{ borderColor: tint }}>
                <span className="re-h font-extrabold text-lg truncate">{name}</span>
                <button onClick={() => setOpen(false)} data-testid="re-mobile-menu-close" aria-label="Close menu"
                  className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition-transform hover:scale-105"
                  style={{ background: tint, color: "var(--re-t)" }}>
                  <X className="h-4 w-4" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                {links.map(([to, l]) => {
                  const Icon = MENU_ICONS[l] || ChevronRight;
                  return (
                    <button key={l} onClick={() => { go(to); setOpen(false); }} data-testid={`re-nav-${l.toLowerCase().replace(/[\s/]+/g, "-")}`} className="re-mitem">
                      <span className="re-micon"><Icon className="h-4 w-4" /></span>
                      <span className="flex-1">{l}</span>
                      <ChevronRight className="h-4 w-4 opacity-40" />
                    </button>
                  );
                })}
              </nav>
              <div className="p-4 border-t shrink-0 space-y-2" style={{ borderColor: tint }}>
                <Link to="/login" onClick={() => setOpen(false)} data-testid="re-mobile-menu-orglogin" className="re-mitem" style={{ border: `1px solid ${tint}` }}>
                  <span className="re-micon"><Users className="h-4 w-4" /></span><span className="flex-1">Org / Client Login</span>
                </Link>
                <button onClick={() => { go("/account"); setOpen(false); }} data-testid="re-mobile-menu-account" className="re-btn w-full h-11 text-sm">{cust ? "My Account" : "Login / Register"}</button>
              </div>
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

function Footer({ data, vars, go, wa }) {
  const info = { ...(data.settings || {}), ...(data.content?.contact || {}) };
  const socials = data.content?.socials || {};
  const footerText = data.content?.footer_text;
  const SOCIALS = [["facebook", "Facebook"], ["instagram", "Instagram"], ["youtube", "YouTube"], ["twitter", "Twitter"], ["linkedin", "LinkedIn"]];
  return (
    <footer className="mt-16 border-t" style={{ background: "color-mix(in srgb, var(--re-t) 96%, #fff)", color: "#fff" }} data-testid="re-footer">
      <div className="max-w-7xl mx-auto px-6 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-sm">
        <div>
          <p className="re-h font-extrabold text-lg">{data.website?.name}</p>
          <p className="text-white/60 mt-2 text-xs leading-relaxed">{footerText || info.description || "Trusted real estate partner — verified properties, transparent documentation."}</p>
          {SOCIALS.some(([k]) => socials[k]) && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="re-socials">
              {SOCIALS.filter(([k]) => socials[k]).map(([k, l]) => (
                <a key={k} href={socials[k]} target="_blank" rel="noreferrer" data-testid={`re-social-${k}`}
                  className="text-[10px] font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/50 transition-colors">{l}</a>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="font-bold mb-3">Properties</p>
          <div className="space-y-2 text-white/70 text-xs">
            {data.all_categories.slice(0, 6).map((c) => <button key={c} onClick={() => go(`/properties?category=${encodeURIComponent(c)}`)} className="block hover:text-white">{c}</button>)}
          </div>
        </div>
        <div>
          <p className="font-bold mb-3">Company</p>
          <div className="space-y-2 text-white/70 text-xs">
            <button onClick={() => go("/about")} className="block hover:text-white">About Us</button>
            <button onClick={() => go("/contact")} className="block hover:text-white">Contact</button>
            <button onClick={() => go("/account")} className="block hover:text-white">My Account</button>
            <Link to="/login" className="block hover:text-white" data-testid="re-footer-orglogin">Org / Client Login</Link>
          </div>
        </div>
        <div>
          <p className="font-bold mb-3">Contact</p>
          <div className="space-y-2 text-white/70 text-xs">
            {info.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{info.phone}</p>}
            {info.email && <p>{info.email}</p>}
            {info.address && <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5" />{info.address}</p>}
            {wa && <a href={`https://wa.me/${String(wa).replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#25D366] font-bold"><MessageCircle className="h-3.5 w-3.5" />WhatsApp Us</a>}
          </div>
        </div>
      </div>
      <p className="text-center text-[11px] text-white/40 pb-6">© {new Date().getFullYear()} {data.website?.name} · Powered by MILTROO</p>
    </footer>
  );
}

export function PropCard({ p, vars, go, onEnquire, saved, onSave }) {
  const loc = p.location || {};
  const ar = p.areas || {};
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="re-card group cursor-pointer" data-testid={`re-prop-${p.id}`}
      onClick={() => go(`/property/${p.id}`)}>
      <div className="relative aspect-[4/3] overflow-hidden" style={{ background: "color-mix(in srgb, var(--re-p) 12%, #eee)" }}>
        {p.images?.[0]
          ? <img src={p.images[0]} alt={p.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="h-full w-full flex items-center justify-center"><Home className="h-10 w-10 opacity-20" /></div>}
        <span className="absolute top-3 left-3 text-white text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-full" style={{ background: "var(--re-p)" }}>{p.category}</span>
        {onSave && (
          <button onClick={(e) => { e.stopPropagation(); onSave(p); }} data-testid={`re-save-${p.id}`}
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 flex items-center justify-center shadow hover:scale-110 transition-transform">
            <Heart className={`h-4 w-4 ${saved ? "fill-red-500 text-red-500" : ""}`} style={{ color: saved ? undefined : "var(--re-t)" }} />
          </button>)}
        <span className="absolute bottom-3 left-3 text-[10px] font-extrabold px-2.5 py-1 rounded-md text-white"
          style={{ background: p.status === "Sold" ? "#dc2626" : "#16a34a" }}>{p.status}</span>
      </div>
      <div className="p-4">
        <p className="re-h font-extrabold text-[15px] leading-snug line-clamp-1">{p.title}</p>
        <p className="text-xs opacity-60 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" />{[loc.area, loc.district, loc.state].filter(Boolean).join(", ") || "—"}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2.5 text-[11px] font-semibold opacity-75">
          {ar.total_sqyds > 0 && <span className="flex items-center gap-1"><Ruler className="h-3 w-3" />{ar.total_sqyds} sq.yds</span>}
          {p.bhk > 0 && <span className="flex items-center gap-1"><BedDouble className="h-3 w-3" />{p.bhk} BHK</span>}
          {p.bathrooms > 0 && <span className="flex items-center gap-1"><Bath className="h-3 w-3" />{p.bathrooms} Bath</span>}
          {p.facing && <span className="flex items-center gap-1"><Compass className="h-3 w-3" />{p.facing}</span>}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/5">
          <div>
            <p className="re-h font-extrabold text-lg" style={{ color: "var(--re-p)" }}>{inr(p.price)}{p.negotiable && <span className="text-[10px] font-bold opacity-50 ml-1">Neg.</span>}</p>
            {p.price_per_sqyd > 0 && <p className="text-[10px] opacity-50">{inr(p.price_per_sqyd)}/sq.yd</p>}
          </div>
          <div className="flex gap-1.5">
            {onEnquire && <button onClick={(e) => { e.stopPropagation(); onEnquire(p); }} data-testid={`re-enquire-${p.id}`} className="re-btn h-8 px-3.5 text-[11px]">Enquire</button>}
            <span className="re-chip h-8 px-3.5 text-[11px] inline-flex items-center gap-1">Details <ChevronRight className="h-3 w-3" /></span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function SearchBar({ data, go, big }) {
  const [f, setF] = useState({ q: "", category: "", district: "", facing: "", status: "" });
  const submit = (e) => {
    e.preventDefault();
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v)));
    go(`/properties?${qs.toString()}`);
  };
  return (
    <form onSubmit={submit} className={`bg-white text-neutral-900 rounded-2xl shadow-2xl p-3 flex flex-wrap gap-2 ${big ? "" : ""}`} data-testid="re-search">
      <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search location, title…" className="flex-1 min-w-[160px] rounded-xl border-neutral-200" data-testid="re-search-q" />
      <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v === "all" ? "" : v })}>
        <SelectTrigger className="w-40 rounded-xl" data-testid="re-search-cat"><SelectValue placeholder="Property Type" /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Types</SelectItem>{data.all_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={f.district} onValueChange={(v) => setF({ ...f, district: v === "all" ? "" : v })}>
        <SelectTrigger className="w-36 rounded-xl" data-testid="re-search-district"><SelectValue placeholder="District" /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Districts</SelectItem>{data.districts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={f.facing} onValueChange={(v) => setF({ ...f, facing: v === "all" ? "" : v })}>
        <SelectTrigger className="w-32 rounded-xl" data-testid="re-search-facing"><SelectValue placeholder="Facing" /></SelectTrigger>
        <SelectContent><SelectItem value="all">Any Facing</SelectItem>{data.facing_options.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
      </Select>
      <button type="submit" className="re-btn h-10 px-6 text-sm flex items-center gap-2" data-testid="re-search-btn" style={{ background: "var(--re-p)" }}><Search className="h-4 w-4" />Search</button>
    </form>
  );
}

const PLOTS_IMG = "https://images.unsplash.com/photo-1774697443203-0f54409d1613?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const HOUSE_IMG = "https://images.unsplash.com/photo-1626249893783-cc4a9f66880a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const APT_IMG = "https://images.unsplash.com/photo-1515263487990-61b07816b324?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const CAT_CARDS = [
  { name: "Sites / Plots", label: "Sites (Plots)", img: PLOTS_IMG, cta: "View Sites" },
  { name: "Individual Houses", label: "Individual Houses", img: HOUSE_IMG, cta: "View Houses" },
  { name: "Apartments", label: "Apartments", img: APT_IMG, cta: "View Apartments" },
];

const FACE_CARDS = [
  { f: "East", Icon: Sunrise }, { f: "West", Icon: Sunset },
  { f: "North", Icon: ArrowUp }, { f: "South", Icon: ArrowDown },
];

const RE_HIGHLIGHTS = [
  { Icon: FileText, t: "Clear Title & Legal Documents" },
  { Icon: MapPin, t: "Prime Locations" },
  { Icon: Route, t: "Good Connectivity" },
  { Icon: Landmark, t: "Bank Loan Assistance" },
  { Icon: TrendingUp, t: "Best Investment Opportunity" },
];

function HeroEnquiry({ slug, data }) {
  const [f, setF] = useState({ name: "", phone: "", interest: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim()) { toast.error("Name and phone are required"); return; }
    setBusy(true);
    try {
      await api.post(`/re/public/${slug}/enquiry`, { name: f.name, phone: f.phone,
        message: [f.interest ? `Interested in: ${f.interest}` : "", f.message].filter(Boolean).join("\n"),
        type: "schedule_visit", source: "hero" });
      toast.success("Submitted! Our team will call you soon.");
      setF({ name: "", phone: "", interest: "", message: "" });
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <motion.form onSubmit={submit} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
      className="bg-white text-neutral-900 rounded-2xl shadow-2xl p-6 space-y-3" data-testid="re-hero-form">
      <p className="re-h font-extrabold text-lg" style={{ color: "var(--re-p)" }}>Get Details / Schedule a Visit</p>
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your Name" className="rounded-xl" data-testid="hero-form-name" />
      <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone Number" className="rounded-xl" data-testid="hero-form-phone" />
      <Select value={f.interest} onValueChange={(v) => setF({ ...f, interest: v })}>
        <SelectTrigger className="rounded-xl" data-testid="hero-form-interest"><SelectValue placeholder="Interested In" /></SelectTrigger>
        <SelectContent>{data.all_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
      <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={2} placeholder="Message (Optional)" className="rounded-xl" data-testid="hero-form-msg" />
      <button type="submit" disabled={busy} className="re-btn w-full h-12 text-sm" data-testid="hero-form-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Submit"}
      </button>
    </motion.form>
  );
}

function HomePage({ slug, data, vars, go, vcfg, cust, wa }) {
  const theme = data.theme || {};
  const content = data.content || {};
  const hero = { ...(theme.hero || {}), ...Object.fromEntries(Object.entries(content.hero || {}).filter(([, v]) => v)) };
  const sec = (k) => content.sections?.[k] !== false;
  const vis = (k) => (sec(k) ? undefined : { display: "none" });
  const testimonials = sec("testimonials") ? (content.testimonials || []).filter((t) => t.name && t.text) : [];
  const gallery = sec("gallery") ? (content.gallery || []) : [];
  const faqs = sec("faqs") ? (content.faqs || []).filter((f) => f.q && f.a) : [];
  const [enq, setEnq] = useState(null);
  const [dist, setDist] = useState("");
  const save = async (p) => {
    if (!cust) { toast.error("Login to save properties"); go("/account"); return; }
    try { const { data: r } = await api.post(`/re/public/${slug}/my/saved/${p.id}`, {}, { headers: { Authorization: `Bearer ${getCustomerToken(slug)}` } }); toast.success(r.saved ? "Saved" : "Removed"); }
    catch { toast.error("Login to save properties"); }
  };
  const heroImg = hero.image || data.featured?.[0]?.images?.[0];
  const shown = dist ? data.latest.filter((p) => (p.location || {}).district === dist) : data.latest;
  const mapQ = dist || data.districts[0] || "India";
  const pill = (active) => `px-5 py-2 rounded-full text-xs font-extrabold transition-all ${active ? "text-white shadow-lg" : "re-chip hover:shadow-md"}`;
  return (
    <div>
      {/* HERO + SIDE ENQUIRY FORM (reference layout) */}
      <section className="relative overflow-hidden" data-testid="re-hero" style={vis("hero")}>
        <div className="relative min-h-[68vh] flex items-center">
          {heroImg && <img src={heroImg} alt="" className="absolute inset-0 h-full w-full object-cover re-kenburns" />}
          <div className="absolute inset-0" style={{ background: "linear-gradient(95deg, rgba(0,0,0,.72) 20%, rgba(0,0,0,.28) 60%, rgba(0,0,0,.15))" }} />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20 w-full grid lg:grid-cols-[1fr_380px] gap-10 items-center">
            <div className="text-white max-w-xl">
              <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="inline-block text-[11px] font-extrabold uppercase tracking-widest px-4 py-1.5 rounded-full"
                style={{ background: "var(--re-a)", color: "#111" }}>{hero.badge || "Real Estate"}</motion.span>
              <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
                className="re-h font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-5 drop-shadow-lg"
                data-testid="re-hero-title">{hero.headline || "Find Your Dream Property"}</motion.h1>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.16 }} className="text-white/85 mt-4 max-w-md font-semibold">
                {hero.subline || "Residential Sites, Individual Houses & Apartments in the Best Locations"}
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }} className="flex flex-wrap gap-3 mt-7">
                <button onClick={() => go("/properties")} className="re-btn h-12 px-7 text-sm flex items-center gap-2" data-testid="re-hero-browse">
                  {hero.cta1 || "Explore Properties"} <ChevronRight className="h-4 w-4" />
                </button>
              </motion.div>
            </div>
            <HeroEnquiry slug={slug} data={data} />
          </div>
        </div>
      </section>

      {/* SEARCH */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-8 relative z-10" style={vis("search")}><SearchBar data={data} go={go} big /></div>

      {/* CATEGORY CARDS + LOCATION MAP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="re-categories" style={vis("categories")}>
        {CAT_CARDS.map((c) => (
          <button key={c.name} onClick={() => go(`/properties?category=${encodeURIComponent(c.name)}`)}
            data-testid={`re-cat-${c.name.toLowerCase().replace(/[\s/()]+/g, "-")}`}
            className="relative h-56 rounded-2xl overflow-hidden group text-left shadow-lg">
            <img src={c.img} alt={c.label} loading="lazy" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
            <p className="absolute top-4 left-4 re-h text-white font-extrabold text-xl leading-tight drop-shadow-md">{c.label}</p>
            <span className="absolute bottom-4 left-4 bg-white text-neutral-900 text-xs font-extrabold px-4 py-2 rounded-full flex items-center gap-1.5 group-hover:gap-2.5 transition-all shadow">
              {c.cta} <ChevronRight className="h-3.5 w-3.5" /></span>
          </button>
        ))}
        <div className="relative h-56 rounded-2xl overflow-hidden shadow-lg border border-black/10 bg-white" data-testid="re-map-card">
          <iframe title="Location Map" className="w-full h-full border-0" loading="lazy"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQ)}&output=embed`} />
          <span className="absolute top-4 left-4 text-white text-xs font-extrabold px-3 py-1.5 rounded-full flex items-center gap-1.5" style={{ background: "var(--re-p)" }}>
            <MapPin className="h-3.5 w-3.5" /> Location Map</span>
        </div>
      </section>

      {/* FACING CARDS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="re-facing" style={vis("facing")}>
        {FACE_CARDS.map(({ f, Icon }) => (
          <button key={f} onClick={() => go(`/properties?facing=${f}`)} data-testid={`re-face-${f.toLowerCase()}`}
            className="relative h-40 rounded-2xl overflow-hidden group shadow-lg">
            <img src={PLOTS_IMG} alt={`${f} facing`} loading="lazy" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-black/45 group-hover:bg-black/55 transition-colors" />
            <span className="absolute top-1/2 left-5 -translate-y-1/2 h-14 w-14 rounded-full bg-white flex items-center justify-center shadow-lg">
              <Icon className="h-6 w-6" style={{ color: "var(--re-p)" }} /></span>
            <span className="absolute top-1/2 right-5 -translate-y-1/2 text-white re-h font-extrabold text-lg text-right leading-tight drop-shadow-md">
              {f.toUpperCase()}<br />FACING</span>
          </button>
        ))}
      </section>

      {/* DISTRICT-WISE PROPERTIES + HIGHLIGHTS RAIL */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-[1fr_320px] gap-6" data-testid="re-districts" style={vis("districts")}>
        <div className="min-w-0">
          <h2 className="re-h font-extrabold text-2xl sm:text-3xl tracking-tight">District / Location Wise Properties</h2>
          <div className="flex flex-wrap gap-2 mt-5">
            <button onClick={() => setDist("")} data-testid="re-dist-all" style={dist === "" ? { background: "var(--re-p)" } : undefined} className={pill(dist === "")}>All</button>
            {data.districts.map((d) => (
              <button key={d} onClick={() => setDist(d)} data-testid={`re-dist-${d.toLowerCase().replace(/\s+/g, "-")}`}
                style={dist === d ? { background: "var(--re-p)" } : undefined} className={pill(dist === d)}>{d}</button>
            ))}
          </div>
          {shown.length === 0 ? (
            <p className="text-sm opacity-60 mt-8">No properties in this location yet — check other locations.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5 mt-6">
              {shown.slice(0, 6).map((p) => <PropCard key={p.id} p={p} vars={vars} go={go} onEnquire={setEnq} onSave={save} />)}
            </div>
          )}
          <button onClick={() => go("/properties")} className="re-btn h-11 px-7 text-sm mt-8" data-testid="re-view-all">View All Properties</button>
        </div>
        <aside className="re-card p-6 h-fit lg:sticky lg:top-24" data-testid="re-highlights">
          <h3 className="re-h font-extrabold text-lg mb-5">Important Highlights</h3>
          <div className="space-y-4">
            {RE_HIGHLIGHTS.map(({ Icon, t }) => (
              <div key={t} className="flex items-center gap-3.5">
                <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "color-mix(in srgb, var(--re-p) 12%, transparent)", color: "var(--re-p)" }}><Icon className="h-5 w-5" /></span>
                <p className="font-bold text-sm">{t}</p>
              </div>
            ))}
          </div>
        </aside>
      </section>

      {/* WHY CHOOSE US */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="re-why" style={vis("why")}>
        <h2 className="re-h font-extrabold text-2xl sm:text-3xl tracking-tight mb-6">Why Choose Us</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {[[ShieldCheck, "Verified Documents", "Every property is documentation-checked before listing."],
            [BadgeCheck, "Transparent Pricing", "Clear price per sq.yd, negotiable flags, no hidden charges."],
            [Phone, "Direct Agent Access", "Call, WhatsApp or schedule a site visit in one tap."]].map(([I, t, d], i) => (
            <motion.div key={t} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="re-card p-6">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "var(--re-p)", color: "#fff" }}><I className="h-5 w-5" /></div>
              <p className="re-h font-bold">{t}</p><p className="text-xs opacity-60 mt-1.5 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="re-contact-strip" style={vis("contact_strip")}>
        <div className="rounded-3xl p-8 sm:p-12 text-white relative overflow-hidden" style={{ background: "linear-gradient(120deg, var(--re-p), color-mix(in srgb, var(--re-p) 55%, #000))" }}>
          <h2 className="re-h font-extrabold text-2xl sm:text-3xl tracking-tight max-w-lg">Ready to visit your future property?</h2>
          <p className="text-white/75 mt-2 text-sm max-w-md">Send an enquiry or schedule a site visit — our team responds within hours.</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={() => go("/contact")} className="h-12 px-7 rounded-full text-sm font-bold bg-white" style={{ color: "var(--re-p)" }} data-testid="re-strip-contact">Contact Us</button>
            {wa && <a href={`https://wa.me/${String(wa).replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="h-12 px-7 rounded-full text-sm font-bold bg-[#25D366] text-white inline-flex items-center gap-2"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
          </div>
        </div>
      </section>

      <EnquireDialog slug={slug} prop={enq} onClose={() => setEnq(null)} cust={cust} />
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="re-testimonials">
          <h2 className="re-h font-extrabold text-2xl sm:text-3xl tracking-tight mb-6">What Our Clients Say</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {testimonials.map((t, i) => (
              <div key={i} className="re-card p-6" data-testid={`re-tst-${i}`}>
                <div className="flex gap-0.5 mb-3">{Array.from({ length: t.rating || 5 }).map((_, s) => <Star key={s} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-sm opacity-75 leading-relaxed">"{t.text}"</p>
                <p className="font-bold text-sm mt-4">— {t.name}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="re-gallery-section">
          <h2 className="re-h font-extrabold text-2xl sm:text-3xl tracking-tight mb-6">Gallery</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {gallery.map((u, i) => <div key={i} className="re-card aspect-[4/3]"><img src={fileUrl(u)} alt="" loading="lazy" className="h-full w-full object-cover" /></div>)}
          </div>
        </section>
      )}
      {faqs.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="re-faqs">
          <h2 className="re-h font-extrabold text-2xl sm:text-3xl tracking-tight mb-6">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="re-card p-5 group" data-testid={`re-faq-${i}`}>
                <summary className="font-bold text-sm cursor-pointer list-none flex items-center justify-between">{f.q}<ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" /></summary>
                <p className="text-sm opacity-70 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}


function Properties({ slug, data, vars, go, cust }) {
  const location = useLocation();
  const paramsQ = new URLSearchParams(location.search);
  const [props, setProps] = useState(null);
  const [total, setTotal] = useState(0);
  const [enq, setEnq] = useState(null);
  const [savedIds, setSavedIds] = useState([]);
  const [f, setF] = useState({
    q: paramsQ.get("q") || "", category: paramsQ.get("category") || "", district: paramsQ.get("district") || "",
    facing: paramsQ.get("facing") || "", status: paramsQ.get("status") || "", min_price: paramsQ.get("min_price") || "",
    max_price: paramsQ.get("max_price") || "", bhk: paramsQ.get("bhk") || "", sort: "newest",
  });

  const load = (ff = f) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(ff).filter(([, v]) => v)));
    api.get(`/re/public/${slug}/properties?${qs}`).then((r) => { setProps(r.data.properties); setTotal(r.data.total); }).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [location.search]);
  useEffect(() => {
    if (cust) api.get(`/re/public/${slug}/my/saved`, { headers: { Authorization: `Bearer ${getCustomerToken(slug)}` } })
      .then((r) => setSavedIds(r.data.properties.map((p) => p.id))).catch(() => {});
  }, [cust]);

  const save = async (p) => {
    if (!cust) { toast.error("Login to save properties"); go("/account"); return; }
    try {
      const { data: r } = await api.post(`/re/public/${slug}/my/saved/${p.id}`, {}, { headers: { Authorization: `Bearer ${getCustomerToken(slug)}` } });
      setSavedIds((ids) => r.saved ? [...ids, p.id] : ids.filter((i) => i !== p.id));
      toast.success(r.saved ? "Saved to your account" : "Removed from saved");
    } catch { toast.error("Login to save properties"); }
  };

  const sel = (key, label, options, tid) => (
    <Select value={f[key] || "all"} onValueChange={(v) => { const nf = { ...f, [key]: v === "all" ? "" : v }; setF(nf); load(nf); }}>
      <SelectTrigger className="rounded-xl bg-white text-neutral-900" data-testid={tid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent><SelectItem value="all">{label}</SelectItem>{options.map((o) => <SelectItem key={o} value={String(o)}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="re-properties-page">
      <h1 className="re-h font-extrabold text-3xl tracking-tight">{f.category || "All Properties"}</h1>
      <p className="text-sm opacity-60 mt-1">{total} propert{total === 1 ? "y" : "ies"} found</p>

      <div className="re-card p-4 mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="re-filters">
        {sel("category", "All Types", data.all_categories, "re-f-category")}
        {sel("district", "All Districts", data.districts, "re-f-district")}
        {sel("facing", "Any Facing", data.facing_options, "re-f-facing")}
        {sel("status", "Any Status", data.statuses, "re-f-status")}
        {sel("bhk", "Any BHK", [1, 2, 3, 4, 5], "re-f-bhk")}
        <Select value={f.sort} onValueChange={(v) => { const nf = { ...f, sort: v }; setF(nf); load(nf); }}>
          <SelectTrigger className="rounded-xl bg-white text-neutral-900" data-testid="re-f-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[["newest", "Newest"], ["price_low", "Price: Low → High"], ["price_high", "Price: High → Low"], ["area", "Area"], ["featured", "Featured"]].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={f.min_price} onChange={(e) => setF({ ...f, min_price: e.target.value })} onBlur={() => load()} placeholder="Min ₹" className="rounded-xl bg-white text-neutral-900" data-testid="re-f-minp" />
        <Input value={f.max_price} onChange={(e) => setF({ ...f, max_price: e.target.value })} onBlur={() => load()} placeholder="Max ₹" className="rounded-xl bg-white text-neutral-900" data-testid="re-f-maxp" />
        <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search area / title…" className="rounded-xl bg-white text-neutral-900 col-span-2" data-testid="re-f-q" />
        <Button onClick={() => load()} className="re-btn border-0 rounded-xl" data-testid="re-f-apply">Apply</Button>
        <Button variant="outline" onClick={() => { const z = { q: "", category: "", district: "", facing: "", status: "", min_price: "", max_price: "", bhk: "", sort: "newest" }; setF(z); load(z); }} className="rounded-xl" data-testid="re-f-reset">Reset</Button>
      </div>

      {!props ? <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin" /></div>
        : props.length === 0 ? <p className="py-16 text-center text-sm opacity-60">No properties match your filters.</p>
        : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {props.map((p) => <PropCard key={p.id} p={p} vars={vars} go={go} onEnquire={setEnq} onSave={save} saved={savedIds.includes(p.id)} />)}
          </div>}
      <EnquireDialog slug={slug} prop={enq} onClose={() => setEnq(null)} cust={cust} />
    </div>
  );
}

function Detail({ slug, pid, data, vars, go, cust, wa }) {
  const [p, setP] = useState(null);
  const [enq, setEnq] = useState(false);
  const [img, setImg] = useState(0);
  useEffect(() => {
    setP(null); setImg(0);
    api.get(`/re/public/${slug}/properties/${pid}`, { headers: getCustomerToken(slug) ? { Authorization: `Bearer ${getCustomerToken(slug)}` } : {} })
      .then((r) => setP(r.data)).catch(() => setP({ nf: true }));
  }, [pid]);
  if (!p) return <div className="py-20 flex justify-center"><Loader2 className="h-7 w-7 animate-spin" /></div>;
  if (p.nf) return <div className="py-20 text-center"><p className="re-h font-extrabold text-2xl">Property not found</p><button onClick={() => go("/properties")} className="re-btn h-11 px-6 mt-4 text-sm">All Properties</button></div>;
  const loc = p.location || {};
  const ar = p.areas || {};
  const agent = p.agent || {};
  const waNum = (agent.whatsapp || wa || "").replace(/\D/g, "");
  const specs = [["Property ID", p.property_id], ["Category", p.category], ["Status", p.status], ["Facing", p.facing],
    ["Total Area", ar.total_sqyds ? `${ar.total_sqyds} sq.yds` : ""], ["Sq. Feet", ar.total_sqft || ""], ["Built-up", ar.builtup_sqft ? `${ar.builtup_sqft} sq.ft` : ""],
    ["Plot Area", ar.plot_area || ""], ["BHK", p.bhk || ""], ["Bathrooms", p.bathrooms || ""], ["Parking", p.parking || ""],
    ["Floor", p.floor ? `${p.floor} / ${p.total_floors || "—"}` : ""], ["Road Width", p.road_width], ["Property Age", p.property_age],
    ["Possession", p.possession], ["Furnished", p.furnished]].filter(([, v]) => v !== "" && v != null && v !== 0);
  const mapQ = p.map?.lat && p.map?.lng ? `${p.map.lat},${p.map.lng}` : (p.map?.address || [loc.area, loc.district, loc.state].filter(Boolean).join(", "));
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="re-detail">
      <button onClick={() => go("/properties")} className="text-xs font-bold opacity-60 hover:opacity-100 mb-4">← All Properties</button>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8 items-start">
        <div className="space-y-6 min-w-0">
          {/* GALLERY */}
          <div>
            <div className="re-card aspect-video" data-testid="re-gallery">
              {p.images?.[img]
                ? <img src={p.images[img]} alt={p.title} className="h-full w-full object-cover" />
                : <div className="h-full w-full flex items-center justify-center" style={{ background: "color-mix(in srgb, var(--re-p) 10%, #eee)" }}><Home className="h-14 w-14 opacity-20" /></div>}
            </div>
            {p.images?.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {p.images.map((u, i) => (
                  <button key={i} onClick={() => setImg(i)} data-testid={`re-thumb-${i}`}
                    className={`h-16 w-24 rounded-lg overflow-hidden shrink-0 ring-2 transition-all ${i === img ? "ring-[var(--re-p)]" : "ring-transparent opacity-60 hover:opacity-100"}`}>
                    <img src={u} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* VIDEOS */}
          {p.videos?.length > 0 && (
            <div className="space-y-3" data-testid="re-videos">
              <h3 className="re-h font-extrabold text-lg">Property Videos</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {p.videos.map((v, i) => {
                  const yt = ytEmbed(v.url); const ig = igEmbed(v.url);
                  return (
                    <div key={i} className="re-card aspect-video" data-testid={`re-video-${i}`}>
                      {yt ? <iframe src={yt} className="w-full h-full" allowFullScreen title={`video-${i}`} />
                        : ig ? <iframe src={ig} className="w-full h-full" allowFullScreen title={`video-${i}`} />
                        : <video src={v.url} controls className="w-full h-full" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* SPECS */}
          <div className="re-card p-6" data-testid="re-specs">
            <h3 className="re-h font-extrabold text-lg mb-4">Property Details</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
              {specs.map(([k, v]) => <div key={k}><p className="text-[10px] font-bold uppercase tracking-wider opacity-45">{k}</p><p className="text-sm font-bold mt-0.5">{v}</p></div>)}
            </div>
          </div>
          {p.highlights?.length > 0 && (
            <div className="re-card p-6" data-testid="re-highlights">
              <h3 className="re-h font-extrabold text-lg mb-4">Important Highlights</h3>
              <div className="flex flex-wrap gap-2">
                {p.highlights.map((h) => <span key={h} className="re-chip px-3.5 py-1.5 text-xs flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" style={{ color: "var(--re-p)" }} />{h}</span>)}
              </div>
            </div>
          )}
          {p.amenities?.length > 0 && (
            <div className="re-card p-6">
              <h3 className="re-h font-extrabold text-lg mb-4">Amenities</h3>
              <div className="flex flex-wrap gap-2">{p.amenities.map((a) => <span key={a} className="re-chip px-3.5 py-1.5 text-xs">{a}</span>)}</div>
            </div>
          )}
          {p.description && (
            <div className="re-card p-6" data-testid="re-description">
              <h3 className="re-h font-extrabold text-lg mb-3">Description</h3>
              <p className="text-sm opacity-70 whitespace-pre-wrap leading-relaxed">{p.description}</p>
            </div>
          )}
          {p.documents?.length > 0 && (
            <div className="re-card p-6" data-testid="re-documents">
              <h3 className="re-h font-extrabold text-lg mb-4">Documents</h3>
              <div className="space-y-2">
                {p.documents.map((d, i) => (
                  <a key={i} href={d.url} target="_blank" rel="noreferrer" data-testid={`re-doc-${i}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-black/10 hover:shadow-md transition-shadow">
                    <FileText className="h-4 w-4" style={{ color: "var(--re-p)" }} /><span className="text-sm font-bold flex-1">{d.name}</span><Download className="h-4 w-4 opacity-40" />
                  </a>
                ))}
              </div>
            </div>
          )}
          {/* MAP */}
          {mapQ && (
            <div className="re-card overflow-hidden" data-testid="re-map">
              <iframe title="map" className="w-full h-72 border-0" loading="lazy"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQ)}&output=embed`} />
            </div>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-4 lg:sticky lg:top-24">
          <div className="re-card p-6" data-testid="re-detail-card">
            <span className="text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-full text-white" style={{ background: "var(--re-p)" }}>{p.category}</span>
            <h1 className="re-h font-extrabold text-2xl tracking-tight mt-3 leading-snug">{p.title}</h1>
            <p className="text-sm opacity-60 mt-1.5 flex items-center gap-1.5"><MapPin className="h-4 w-4" />{[loc.area, loc.city, loc.district, loc.state, loc.pincode].filter(Boolean).join(", ") || "—"}</p>
            <p className="re-h font-extrabold text-3xl mt-4" style={{ color: "var(--re-p)" }}>{inr(p.price)}</p>
            <div className="flex gap-2 mt-1 text-[11px] font-bold opacity-60">
              {p.price_per_sqyd > 0 && <span>{inr(p.price_per_sqyd)} / sq.yd</span>}
              {p.negotiable && <span className="text-emerald-600">Negotiable</span>}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-5">
              <button onClick={() => setEnq(true)} className="re-btn h-11 text-sm col-span-2" data-testid="re-detail-enquire">Enquire Now</button>
              {agent.phone && <a href={`tel:${agent.phone}`} className="re-chip h-11 text-xs font-bold flex items-center justify-center gap-1.5" data-testid="re-detail-call"><Phone className="h-3.5 w-3.5" />Call Agent</a>}
              {waNum && <a href={`https://wa.me/${waNum}?text=${encodeURIComponent(`Hi, I'm interested in "${p.title}" (${p.property_id || ""})`)}`} target="_blank" rel="noreferrer"
                className="h-11 rounded-full text-xs font-bold bg-[#25D366] text-white flex items-center justify-center gap-1.5" data-testid="re-detail-whatsapp"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</a>}
              <button onClick={() => setEnq(true)} className="re-chip h-11 text-xs font-bold col-span-2 flex items-center justify-center gap-1.5" data-testid="re-detail-visit"><CalendarClock className="h-3.5 w-3.5" />Schedule a Site Visit</button>
            </div>
            {(agent.name || agent.phone) && (
              <div className="mt-5 pt-4 border-t border-black/10 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: "var(--re-p)" }}>{(agent.name || "A")[0]}</div>
                <div><p className="text-sm font-bold">{agent.name || "Property Agent"}</p><p className="text-[11px] opacity-50">{agent.phone}</p></div>
              </div>
            )}
            <p className="text-[10px] opacity-40 mt-4 flex items-center gap-1"><Eye className="h-3 w-3" />{p.views} views · ID {p.property_id}</p>
          </div>
          {p.related?.length > 0 && (
            <div className="space-y-3">
              <h3 className="re-h font-extrabold">Similar Properties</h3>
              {p.related.map((r) => (
                <button key={r.id} onClick={() => go(`/property/${r.id}`)} className="re-card w-full flex gap-3 p-3 text-left" data-testid={`re-related-${r.id}`}>
                  <div className="h-16 w-20 rounded-lg overflow-hidden shrink-0 bg-black/5">{r.images?.[0] && <img src={r.images[0]} className="h-full w-full object-cover" alt="" />}</div>
                  <div className="min-w-0"><p className="text-sm font-bold line-clamp-1">{r.title}</p>
                    <p className="text-[11px] opacity-50">{(r.location || {}).district || ""}</p>
                    <p className="text-sm font-extrabold mt-0.5" style={{ color: "var(--re-p)" }}>{inr(r.price)}</p></div>
                </button>
              ))}
            </div>
          )}
        </aside>
      </div>
      <EnquireDialog slug={slug} prop={p} onClose={() => setEnq(false)} cust={cust} open={enq} />
    </div>
  );
}

export function EnquireDialog({ slug, prop, onClose, cust, open }) {
  const isOpen = open !== undefined ? open : !!prop;
  const [f, setF] = useState({ name: "", phone: "", email: "", message: "", type: "enquire" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (cust) setF((x) => ({ ...x, name: cust.name || x.name, email: cust.email || x.email, phone: cust.phone || x.phone })); }, [cust, isOpen]);
  const submit = async () => {
    if (!f.name.trim() || !f.phone.trim()) { toast.error("Name and phone are required"); return; }
    setBusy(true);
    try {
      await api.post(`/re/public/${slug}/enquiry`, { ...f, property_id: prop?.id || "", source: prop ? "property_page" : "contact" },
        { headers: getCustomerToken(slug) ? { Authorization: `Bearer ${getCustomerToken(slug)}` } : {} });
      toast.success("Enquiry sent! Our team will contact you soon.");
      onClose(); setF({ name: cust?.name || "", phone: cust?.phone || "", email: cust?.email || "", message: "", type: "enquire" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="re-enquire-dialog">
        <DialogHeader><DialogTitle className="re-h">{prop ? `Enquire — ${prop.title}` : "Enquire Now"}</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your Name *" data-testid="enq-name" />
            <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone *" data-testid="enq-phone" />
          </div>
          <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" data-testid="enq-email" />
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
            <SelectTrigger data-testid="enq-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[["enquire", "General Enquiry"], ["contact_agent", "Contact Agent"], ["schedule_visit", "Schedule a Site Visit"], ["price", "Price Negotiation"]].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={3} placeholder="Message (optional)" data-testid="enq-msg" />
          <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-full font-bold" data-testid="enq-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Enquiry"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function About({ data, vars, go }) {
  const info = data.settings || {};
  const about = data.content?.about || {};
  return (
    <div className="max-w-4xl mx-auto px-6 py-14" data-testid="re-about">
      <h1 className="re-h font-extrabold text-4xl tracking-tight">{about.title || `About ${data.website?.name}`}</h1>
      <p className="mt-5 text-base opacity-70 leading-relaxed whitespace-pre-wrap">{about.text || info.description || "We are a trusted real estate business helping families and investors find verified plots, homes and commercial spaces."}</p>
      <div className="grid sm:grid-cols-3 gap-4 mt-10">
        {[[ShieldCheck, "Verified Titles"], [Star, "Customer First"], [CheckCircle2, "End-to-End Support"]].map(([I, t]) => (
          <div key={t} className="re-card p-6 text-center"><I className="h-7 w-7 mx-auto" style={{ color: "var(--re-p)" }} /><p className="font-bold text-sm mt-3">{t}</p></div>
        ))}
      </div>
      <button onClick={() => go("/contact")} className="re-btn h-12 px-8 mt-10 text-sm" data-testid="re-about-contact">Talk to Us</button>
    </div>
  );
}

function Contact({ slug, data, vars, wa }) {
  const info = { ...(data.settings || {}), ...(data.content?.contact || {}) };
  return (
    <div className="max-w-5xl mx-auto px-6 py-14 grid lg:grid-cols-2 gap-10" data-testid="re-contact">
      <div>
        <h1 className="re-h font-extrabold text-4xl tracking-tight">Contact Us</h1>
        <div className="space-y-4 mt-8 text-sm">
          {info.phone && <a href={`tel:${info.phone}`} className="flex items-center gap-3 font-bold"><Phone className="h-4 w-4" style={{ color: "var(--re-p)" }} />{info.phone}</a>}
          {info.email && <p className="flex items-center gap-3 font-bold"><Bell className="h-4 w-4" style={{ color: "var(--re-p)" }} />{info.email}</p>}
          {info.address && <p className="flex items-start gap-3 font-bold"><MapPin className="h-4 w-4 mt-0.5" style={{ color: "var(--re-p)" }} />{info.address}</p>}
          {wa && <a href={`https://wa.me/${String(wa).replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#25D366] text-white font-bold text-sm" data-testid="re-contact-wa"><MessageCircle className="h-4 w-4" />WhatsApp Us</a>}
        </div>
      </div>
      <div className="re-card p-6"><EnquiryInline slug={slug} /></div>
    </div>
  );
}

function EnquiryInline({ slug }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim() || !f.phone.trim()) { toast.error("Name and phone are required"); return; }
    setBusy(true);
    try { await api.post(`/re/public/${slug}/enquiry`, { ...f, source: "contact_page" }); toast.success("Enquiry sent!"); setF({ name: "", phone: "", email: "", message: "" }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p className="re-h font-extrabold text-lg">Send an Enquiry</p>
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name *" data-testid="ct-name" />
      <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone *" data-testid="ct-phone" />
      <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" data-testid="ct-email" />
      <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={4} placeholder="Message" data-testid="ct-msg" />
      <Button onClick={submit} disabled={busy} className="re-btn w-full h-11 border-0" data-testid="ct-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}</Button>
    </div>
  );
}

function Account({ slug, data, vars, cust, setCust, go }) {
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("profile");
  const [dash, setDash] = useState({});
  const tok = getCustomerToken(slug);
  const H = { Authorization: `Bearer ${tok}` };

  const loadDash = () => {
    if (!tok) return;
    api.get(`/re/public/${slug}/my/enquiries`, { headers: H }).then((r) => setDash((d) => ({ ...d, enquiries: r.data.enquiries }))).catch(() => {});
    api.get(`/re/public/${slug}/my/saved`, { headers: H }).then((r) => setDash((d) => ({ ...d, saved: r.data.properties }))).catch(() => {});
    api.get(`/re/public/${slug}/my/invoices`, { headers: H }).then((r) => setDash((d) => ({ ...d, invoices: r.data.invoices }))).catch(() => {});
    api.get(`/re/public/${slug}/my/data`, { headers: H }).then((r) => setDash((d) => ({ ...d, custom: r.data.custom, assigned: r.data.assigned_property, notifs: r.data.notifications }))).catch(() => {});
  };
  useEffect(() => { if (cust) loadDash(); }, [cust]);

  const auth = async () => {
    setBusy(true);
    try {
      // unified RE identity: registers/logs in as an RE org CLIENT member (single account
      // powers the client dashboard AND favourites/enquiries via member-token support)
      const url = mode === "register" ? "org/register" : "org/login";
      const payload = mode === "register" ? { name: f.name, email: f.email, password: f.password, mobile: f.phone } : { email: f.email, password: f.password };
      const { data: r } = await api.post(`/re/public/${slug}/${url}`, payload);
      const m = r.member;
      setCustomerSession(slug, r.token, { name: m.full_name, email: m.email, phone: m.mobile || "" });
      localStorage.setItem(`zylo_remember_${slug}`, r.token);
      localStorage.setItem(`zylo_remember_${slug}_info`, JSON.stringify(m));
      setCust({ name: m.full_name, email: m.email, phone: m.mobile || "" });
      toast.success(`Welcome${mode === "register" ? "" : " back"}, ${m.full_name}!`);
      go("/org");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const downloadPdf = async (inv) => {
    try {
      const r = await api.get(`/re/public/${slug}/my/invoices/${inv.id}/pdf`, { headers: H, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch { toast.error("Could not download invoice"); }
  };

  if (!cust) {
    return (
      <div className="max-w-md mx-auto px-6 py-16" data-testid="re-auth">
        <div className="re-card p-8">
          <h1 className="re-h font-extrabold text-2xl tracking-tight text-center">{mode === "login" ? "Customer Login" : "Create Account"}</h1>
          <p className="text-xs opacity-60 text-center mt-1">Save properties, track enquiries & download invoices</p>
          <div className="space-y-3.5 mt-6">
            {mode === "register" && (
              <>
                <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full Name *" data-testid="ra-name" />
                <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" data-testid="ra-phone" />
              </>
            )}
            <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email *" data-testid="ra-email" />
            <Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password *" data-testid="ra-password" />
            <Button onClick={auth} disabled={busy} className="re-btn w-full h-11 border-0" data-testid="ra-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Login" : "Register"}
            </Button>
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="w-full text-center text-xs font-bold opacity-70 hover:opacity-100" data-testid="ra-switch">
              {mode === "login" ? "New here? Create an account" : "Already registered? Login"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const cd = dash.custom;
  const TABS = [["profile", "My Profile"], ["enquiries", "My Enquiries"], ["saved", "Saved Properties"], ["invoices", "Invoices"], ["foryou", "For You"], ["notifs", "Notifications"]];
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="re-account">
      <h1 className="re-h font-extrabold text-3xl tracking-tight">Hello, {cust.name}</h1>
      <p className="text-sm opacity-60">{cust.email}</p>
      <div className="flex flex-wrap gap-2 mt-6">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`re-tab-${k}`}
            className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${tab === k ? "re-btn" : "re-chip"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "profile" && (
          <div className="re-card p-6 max-w-lg space-y-2 text-sm" data-testid="re-profile">
            <p><span className="opacity-50">Name:</span> <b>{cust.name}</b></p>
            <p><span className="opacity-50">Email:</span> <b>{cust.email}</b></p>
            <p><span className="opacity-50">Phone:</span> <b>{cust.phone || "—"}</b></p>
          </div>
        )}
        {tab === "enquiries" && (
          <div className="space-y-3" data-testid="re-my-enquiries">
            {(dash.enquiries || []).length === 0 && <p className="text-sm opacity-50">No enquiries yet.</p>}
            {(dash.enquiries || []).map((e) => (
              <div key={e.id} className="re-card p-4 flex flex-wrap justify-between gap-3">
                <div><p className="font-bold text-sm">{e.property_name || "General enquiry"}</p><p className="text-xs opacity-60 mt-0.5">{e.message || e.type}</p></div>
                <span className="re-chip px-3 py-1 text-[10px] uppercase font-extrabold h-fit">{e.status}</span>
              </div>
            ))}
          </div>
        )}
        {tab === "saved" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="re-my-saved">
            {(dash.saved || []).length === 0 && <p className="text-sm opacity-50">No saved properties yet — tap the heart on any property.</p>}
            {(dash.saved || []).map((p) => (
              <div key={p.id} className="re-card p-4">
                <p className="font-bold text-sm line-clamp-1">{p.title}</p>
                <p className="text-xs opacity-50">{(p.location || {}).district || ""}</p>
                <p className="font-extrabold mt-1" style={{ color: "var(--re-p)" }}>{inr(p.price)}</p>
              </div>
            ))}
          </div>
        )}
        {tab === "invoices" && (
          <div className="space-y-3" data-testid="re-my-invoices">
            {(dash.invoices || []).length === 0 && <p className="text-sm opacity-50">No invoices yet.</p>}
            {(dash.invoices || []).map((i) => (
              <div key={i.id} className="re-card p-4 flex flex-wrap items-center gap-4" data-testid={`re-inv-${i.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-bold text-sm">{i.number}</p>
                  <p className="text-xs opacity-50">{i.invoice_date}{i.period ? ` · ${i.period}` : ""}</p>
                </div>
                <p className="font-extrabold">{inr2(i.total)}</p>
                <span className={`re-chip px-3 py-1 text-[10px] uppercase font-extrabold ${i.status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>{i.status.replace("_", " ")}</span>
                <Button size="sm" variant="outline" onClick={() => downloadPdf(i)} className="rounded-full font-bold" data-testid={`re-inv-pdf-${i.id}`}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
              </div>
            ))}
          </div>
        )}
        {tab === "foryou" && (
          <div className="space-y-4" data-testid="re-my-custom">
            {!cd && <p className="text-sm opacity-50">No personalized offers yet.</p>}
            {cd && (
              <div className="re-card p-6 space-y-3 text-sm">
                {cd.custom_message && <p className="re-h font-bold text-base">{cd.custom_message}</p>}
                {cd.special_offer && <p className="flex items-center gap-2"><Sparkles className="h-4 w-4" style={{ color: "var(--re-p)" }} /><b>Special Offer:</b> {cd.special_offer}</p>}
                {cd.special_price && <p><b>Special Price:</b> {cd.special_price}</p>}
                {cd.booking_details && <p><b>Booking:</b> {cd.booking_details}</p>}
                {cd.payment_info && <p><b>Payment Info:</b> {cd.payment_info}</p>}
                {(cd.documents || []).length > 0 && (
                  <div className="pt-2 space-y-2">
                    <p className="font-bold text-xs uppercase tracking-wider opacity-50">Your Documents</p>
                    {cd.documents.map((d, i) => <a key={i} href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-bold hover:underline"><FileText className="h-4 w-4" />{d.name}</a>)}
                  </div>
                )}
              </div>
            )}
            {dash.assigned && (
              <div className="re-card p-5">
                <p className="text-xs font-bold uppercase tracking-wider opacity-50 mb-2">Assigned Property</p>
                <p className="re-h font-extrabold">{dash.assigned.title}</p>
                <p className="font-extrabold mt-1" style={{ color: "var(--re-p)" }}>{inr(dash.assigned.price)}</p>
              </div>
            )}
          </div>
        )}
        {tab === "notifs" && (
          <div className="space-y-2.5" data-testid="re-my-notifs">
            {(dash.notifs || []).length === 0 && <p className="text-sm opacity-50">No notifications.</p>}
            {(dash.notifs || []).map((n) => (
              <div key={n.id} className="re-card p-4"><p className="font-bold text-sm">{n.title}</p><p className="text-xs opacity-60 mt-0.5">{n.body}</p></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
